import React from "react";
import { useTranslation } from "react-i18next";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi2";
import {
  MAX_YOUTUBE_VIDEOS,
  getYouTubeEmbedUrl,
  isYouTubeUrl,
  normalizeYouTubeUrl,
} from "../utils/youtubeLinks";

interface YoutubeVideosFormEditorProps {
  links: string[];
  onChange: (links: string[]) => void;
  error?: string;
  fieldIdPrefix?: string;
}

export const YoutubeVideosFormEditor: React.FC<YoutubeVideosFormEditorProps> = ({
  links,
  onChange,
  error,
  fieldIdPrefix = "youtube-form",
}) => {
  const { t } = useTranslation();
  const slots = links.length > 0 ? links : [""];

  const updateLinkAt = (index: number, value: string) => {
    onChange(slots.map((link, i) => (i === index ? value : link)));
  };

  const removeLinkAt = (index: number) => {
    const next = slots.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : [""]);
  };

  const addLinkSlot = () => {
    if (slots.length >= MAX_YOUTUBE_VIDEOS) return;
    onChange([...slots, ""]);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t("youtubeVideosHelp") || "Add up to 10 YouTube links for this song."}
        </p>
        <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
          {slots.filter((l) => l.trim()).length}/{MAX_YOUTUBE_VIDEOS}
        </span>
      </div>

      {slots.map((link, index) => {
        const normalized = normalizeYouTubeUrl(link);
        const previewEmbed =
          normalized && isYouTubeUrl(normalized) ? getYouTubeEmbedUrl(normalized) : null;

        return (
          <div
            key={index}
            className="rounded-xl border border-gray-200 dark:border-gray-600 p-3 space-y-3"
          >
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <label
                  htmlFor={`${fieldIdPrefix}-${index}`}
                  className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5"
                >
                  {t("youtube_link") || "YouTube Link"} {index + 1}
                </label>
                <input
                  id={`${fieldIdPrefix}-${index}`}
                  type="url"
                  value={link}
                  onChange={(e) => updateLinkAt(index, e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-[#5932EA]"
                />
              </div>
              <button
                type="button"
                onClick={() => removeLinkAt(index)}
                className="mt-6 p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0"
                aria-label={t("removeYouTubeVideo") || "Remove video"}
              >
                <HiOutlineTrash className="w-4 h-4" />
              </button>
            </div>

            {previewEmbed && (
              <div className="relative w-full aspect-video overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700">
                <iframe
                  width="100%"
                  height="100%"
                  className="absolute inset-0 w-full h-full"
                  src={previewEmbed}
                  title={`YouTube preview ${index + 1}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </div>
        );
      })}

      {slots.length < MAX_YOUTUBE_VIDEOS && (
        <button
          type="button"
          onClick={addLinkSlot}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#5932EA] hover:bg-[#5932EA]/10 dark:hover:bg-[#5932EA]/20 transition-colors"
        >
          <HiOutlinePlus className="w-4 h-4" />
          {t("addAnotherYouTubeVideo") || "Add another video"}
        </button>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};
